import type { PostHog } from 'posthog-js';
import type { TFunction } from 'src/hooks/use-locales/types';

import isUndefinedNullOrEmpty from '@ahd/shared/tools/is-undefined-null-or-empty';
import outputConsole from '@ahd/shared/tools/output-console';
import { FrontendEnvironment } from '@ahd/shared/types/misc/environments';
import { Component } from 'react';
import { withTranslation } from 'react-i18next';

import withPosthog from 'src/provider/with-post-hog';

import { type ConfigObject, DebugLevel } from '../_configs/types';
import { CarDetailsErrorCauses } from '../_tools/errors/car-details';
import { CarListErrorCauses } from '../_tools/errors/car-list';
import { ConfigErrorCauses } from '../_tools/errors/config';
import { errorHandler } from '../_tools/errors/handler';
import { Errors, type FunctionOutputTypeError } from '../_tools/types/function-output-type';
import { DBLogSource } from '../_tools/types/log';
import ErrorCard from '../components/error-card';
import enhancedNavigate from '../utils/tools/enhanced-navigate';
import { APPLICATION_NAME, ENVIRONMENT_FRONTEND } from '../vars';

import type { Modules } from '../modules/_definition/types';

interface IState {
    error?: FunctionOutputTypeError;
}

interface IProps {
    children: any;
    module: Modules;
    posthog: PostHog;
    settings: ConfigObject;
    t: TFunction;
}

class ErrorHandler extends Component<IProps, IState> {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    shouldComponentUpdate() {
        return true;
    }

    async handleError(error: unknown) {
        const returnValue = await errorHandler(
            this?.props?.t,
            this?.props?.settings?.general?.language,
            'ErrorHandler',
            error,
            this?.props?.settings?.general?.idGroup,
            DBLogSource.Fleet,
            this?.props?.settings?.general?.debug,
            ENVIRONMENT_FRONTEND === FrontendEnvironment.LOCALSERVER
        );
        this?.props?.posthog?.captureException?.(error, { service_name: APPLICATION_NAME });
        outputConsole('error', `ErrorHandler`, 'Error occured', returnValue);
        this.setState({ error: returnValue?.output });
    }

    componentDidCatch(error: unknown) {
        this.handleError(error);
    }

    handleErrorAction(action: string) {
        if (action === 'reloadPage') location.reload();
        // else if (action === 'exitError') this?.setState({ error: null });
        else if (action === 'goHome') {
            this?.setState({ error: null });
            enhancedNavigate(this?.props?.settings?.general?.searchPath);
        }
    }

    render() {
        const { children, module, settings, t } = this.props;
        const { error } = this.state;

        if (!isUndefinedNullOrEmpty(error)) {
            return (
                <ErrorCard
                    action={error?.action}
                    collapse={
                        error?.name === Errors.API || error?.name === Errors.ERROR
                            ? {
                                  code: error?.name,
                                  message: error?.message,
                                  stack: ENVIRONMENT_FRONTEND === FrontendEnvironment.LOCALSERVER || settings?.general?.debug >= DebugLevel.ERRORS ? error?.stack : undefined,
                              }
                            : undefined
                    }
                    description={error?.description}
                    heading={error?.heading}
                    level={
                        error?.name === Errors.MODULE_DISABLED || (error?.name === Errors.CONFIG && error?.cause === ConfigErrorCauses.DISABLED)
                            ? 'grey'
                            : (error?.name === Errors.CAR_DETAILS && error?.cause === CarDetailsErrorCauses.NOT_FOUND) ||
                                (error?.name === Errors.CAR_LIST && error?.cause === CarListErrorCauses.NO_RESULTS)
                              ? 'info'
                              : 'error'
                    }
                    module={module}
                    resetErrorState={() => {
                        this.setState({ error: undefined });
                    }}
                    settings={settings}
                    translate={t}
                />
            );
        }

        return children;
    }
}

export default withTranslation()(withPosthog(ErrorHandler));
